import { useState, useMemo, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Typography } from 'antd'
import { SkeletonReport } from '../../features/skeleton'
import { useLabelFilterState } from '../../features/labelFilter'
import { useSectionVisibility } from '../../features/reportSections'
import { UpgradePromptModal } from '../../components/UpgradePromptModal/UpgradePromptModal'
import {
  useNodeUtils,
  useReportHierarchy,
  ReportContent,
  SelectionBanner,
  type ReenterSelectionState,
} from '../../features/report'
import { ReportContainer } from '../styles/ReportPageStyles'
import { useReportData } from './hooks/useReportData'
import { useReportVisibility } from './hooks/useReportVisibility'
import { useIdeaExtras } from './hooks/useIdeaExtras'
import { ReportHeaderBar } from './components/ReportHeaderBar'
import { PresentationOptions } from './components/PresentationOptions'
import { analytics } from '../../utils/analytics'

const { Title } = Typography

export function ReportPage() {
  const { id: mindmapId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showStakeholderRatings, setShowStakeholderRatings] = useState(false)

  useEffect(() => {
    if (mindmapId) analytics.reportViewed({ mindmap_id: mindmapId })
  }, [mindmapId])

  const { isVisible, toggleSection } = useSectionVisibility()
  const { selectedFilterLabels, setSelectedFilterLabels } = useLabelFilterState({ mindmapId })

  const {
    loading,
    mindmap,
    projectLabels,
    nodeLabelMap,
    hasAccess,
    showUpgradeModal,
    handleUpgradeModalClose,
  } = useReportData(mindmapId)

  const { effectiveShouldDisplayNode, nodeFilterId, setSearchParams, exportSelectionIds } =
    useReportVisibility(mindmap, nodeLabelMap, selectedFilterLabels, mindmapId)

  const nodes = mindmap?.content.nodes ?? []
  const edges = mindmap?.content.edges ?? []

  const exportCategoryCounts = useMemo(() => {
    const counts = new Map<string, number>()
    if (!exportSelectionIds) return counts
    for (const nodeId of exportSelectionIds) {
      const node = nodes.find((n) => n.id === nodeId)
      if (!node) continue
      counts.set(node.data.category, (counts.get(node.data.category) ?? 0) + 1)
    }
    return counts
  }, [exportSelectionIds, nodes])

  const handleChangeExportSelection = useCallback(() => {
    if (!mindmapId) return
    const reenterSelection: ReenterSelectionState = {
      nodeIds: exportSelectionIds ? [...exportSelectionIds] : [],
      storageKey: `report-selection-${mindmapId}`,
      destination: `/mindmap/${mindmapId}/report`,
      title: 'Select branches to export',
      subtitle: 'Choose which branches to include in the report',
    }
    void navigate(`/mindmap/${mindmapId}`, { state: { reenterSelection } })
  }, [navigate, mindmapId, exportSelectionIds])

  const { getEffectiveNodeType, getEffectiveNodeData, nodeMap, getChildIdeas, getLabelsForNode } =
    useNodeUtils(nodes, edges, projectLabels, nodeLabelMap, effectiveShouldDisplayNode)

  const { renderIdeaExtras } = useIdeaExtras(
    mindmap,
    nodeMap,
    getEffectiveNodeData,
    showStakeholderRatings,
    isVisible
  )

  const hierarchy = useReportHierarchy(
    nodes,
    edges,
    nodeMap,
    getEffectiveNodeType,
    effectiveShouldDisplayNode,
    getChildIdeas,
    isVisible
  )

  if (loading) {
    return <SkeletonReport />
  }

  if (hasAccess === false) {
    return (
      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={handleUpgradeModalClose}
        featureName="PDF export"
      />
    )
  }

  if (!mindmap) {
    return (
      <ReportContainer>
        <Title level={2}>Report</Title>
        <p>Mindmap not found</p>
      </ReportContainer>
    )
  }

  return (
    <ReportContainer>
      <ReportHeaderBar
        title={mindmap.title}
        onBack={() => void navigate(-1)}
        onExport={() => window.print()}
      />

      <SelectionBanner
        categoryCounts={exportCategoryCounts}
        onChangeSelection={handleChangeExportSelection}
      />

      <PresentationOptions
        nodeFilterId={nodeFilterId}
        setSearchParams={setSearchParams}
        isVisible={isVisible}
        toggleSection={toggleSection}
        showStakeholderRatings={showStakeholderRatings}
        onShowStakeholderRatingsChange={setShowStakeholderRatings}
        projectLabels={projectLabels}
        selectedFilterLabels={selectedFilterLabels}
        onFilterLabelsChange={setSelectedFilterLabels}
      />

      <ReportContent
        ventureNodes={hierarchy.ventureNodes}
        standaloneNeeds={hierarchy.standaloneNeeds}
        standaloneIdeas={hierarchy.standaloneIdeas}
        getEffectiveNodeData={getEffectiveNodeData}
        getVentureNeeds={hierarchy.getVentureNeeds}
        getChildIdeas={getChildIdeas}
        getLabelsForNode={getLabelsForNode}
        renderIdeaExtras={renderIdeaExtras}
        isVisible={isVisible}
        getAllIdeasUnderVenture={hierarchy.getAllIdeasUnderVenture}
        ideasFromStandaloneNeeds={hierarchy.ideasFromStandaloneNeeds}
      />
    </ReportContainer>
  )
}
